import type { Identifier, XYCoord } from 'dnd-core';
import type { FC } from 'react';
import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Row, Card as CardFolder } from 'reactstrap';
import { toast } from 'react-toastify';
import history from '../../../history';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import { DocumentsStateManager } from './DocumentsStateManager';
import { deleteFolderAlert } from './DeleteFolderAlert';
import DocumentsFoldersInterface from '../../../interfaces/DocumentsFoldersInterface';

const ItemTypes = {
  CARD: 'card',
};

const style = {
  marginBottom: '.5rem',
  cursor: 'move',
  display: 'inline-grid',
  minWidth: '240px',
};

export interface CardProps {
  moveCard: (dragIndex: number, hoverIndex: number, idFolder: string) => void;
  key: string;
  folder: DocumentsFoldersInterface;
  id: string;
  documentsState: DocumentsStateManager;
  index: number;
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DragDropFolderCard: FC<CardProps> = ({ id, folder, moveCard, documentsState, index }) => {
  const { isMobile } = useCheckMobileScreen();
  const ref = useRef<HTMLDivElement>(null);
  const [_, setIsLoaded] = useState(false);

  const openFolder = async (folderId: string, folderName: string, event?: boolean) => {
    if (!documentsState.hasFolderAccess(folderId)) {
      return toast.error(`You don't have access to folder: ${folderName}`);
    }
    if (event == false) {
      documentsState.clearData();
      history.push(`${documentsState.state.location}#${folderId}`);
    }
  };

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: ItemTypes.CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      const idFolder = item.id;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex, idFolder);

      // Note: we're mutating the monitor item here!
      // Generally, it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));
  const isDefaultImage = folder.picture.indexOf('default.png') > -1;
  const mediaHeight = isMobile ? 64 : 100;
  const iconSize = isMobile ? 40 : 64;
  return (
    <>
      {documentsState.hasFolderShowPermission() && (
        <div
          className={isMobile ? 'col-6' : 'col-1'}
          ref={ref}
          style={
            isMobile
              ? { ...style, opacity, minWidth: 0, maxWidth: '100%' }
              : { ...style, opacity, minWidth: '235px', maxWidth: '235px' }
          }
          data-handler-id={handlerId}
          id={`folderPosition-${index}`}
          tabIndex={-1}
        >
          <CardFolder>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ height: `${mediaHeight}px`, padding: '0 12px', overflow: 'hidden' }}
              onMouseUp={() => openFolder(folder.id, folder.name, isDragging)}
            >
              {isDefaultImage ? (
                <i className="fas fa-folder" style={{ fontSize: `${iconSize}px`, lineHeight: 1, color: '#5e72e4' }} />
              ) : (
                <img
                  alt={folder.name}
                  className="img-fluid"
                  src={`data:image/jpeg;base64,${folder.pictureBlobBase64}`}
                  style={{ maxHeight: mediaHeight, maxWidth: '100%', width: 'auto' }}
                  onLoad={() => setIsLoaded(true)}
                />
              )}
            </div>
            <div>
              <Row
                style={{
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginLeft: '15px',
                  marginRight: '15px',
                  paddingTop: '10px',
                  paddingBottom: isMobile ? '10px' : '0',
                }}
              >
                <div style={{ textAlign: 'center', width: isMobile ? '100%' : 'auto', lineHeight: 1.3 }}>
                  {folder.name}
                </div>
                {!isMobile &&
                  (documentsState.hasFolderEditPermission() || documentsState.hasFolderEditPermission()) && (
                    <>
                      <div style={{ textAlign: 'center', paddingBottom: '10px', paddingTop: '10px' }}>
                        <div className="btn-group btn-group-sm" role="group" aria-label="Basic example">
                          {documentsState.hasFolderEditPermission() && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                documentsState.modalFolderShow(folder);
                              }}
                            >
                              <i className="far fa-edit text-blue"></i>
                            </button>
                          )}
                          {documentsState.hasFolderDeletePermission() && (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ cursor: 'pointer' }}
                              onClick={async () => {
                                await deleteFolderAlert(folder, documentsState);
                              }}
                            >
                              <i className="far fa-trash-alt text-danger"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
              </Row>
            </div>
          </CardFolder>
        </div>
      )}
    </>
  );
};
